import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import useDevice from '../../hooks/useDevice';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Smartphone, Loader2, ArrowRight, Share, MoreVertical, Monitor, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';
import OnboardingStepIndicator from './OnboardingStepIndicator';

// iter97 (Feb 2026) — the install step now shows FULL device-specific
// instructions and lets the user switch between iPhone / Android / Desktop
// via a segmented picker. The detected platform is pre-selected via the
// shared `useDevice()` hook, but a manager onboarding a phone-first
// worker from their desktop (or vice versa) can flip to the right variant.
// The primary "I've installed ShiftCover365" and "Do this later" actions
// remain unchanged — dismissal persists server-side via
// /users/me/install-status so the screen is never re-shown.

const PLATFORMS = [
  { key: 'ios', label: 'iPhone' },
  { key: 'android', label: 'Android' },
  { key: 'desktop', label: 'Desktop' },
];

export default function InstallPwaStep({ onDone }) {
  const { api, user, refreshUser } = useAuth();
  const { device: detectedPlatform, isStandalone: alreadyStandalone } = useDevice();
  const [platform, setPlatform] = useState(detectedPlatform);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);
  const shownAuditFiredRef = useRef(false);
  const autoInstallFiredRef = useRef(false);
  const isManager = user?.role === 'manager';
  const stepIndex = isManager ? 4 : 3;

  // Keep the selection in sync with the shared hook once it hydrates.
  useEffect(() => { setPlatform(detectedPlatform); }, [detectedPlatform]);

  // Analytics: mark that the screen was shown for the detected platform.
  useEffect(() => {
    if (shownAuditFiredRef.current) return;
    shownAuditFiredRef.current = true;
    api.post('/users/me/install-status', { status: 'shown', platform: detectedPlatform }).catch(() => {});
  }, [api, detectedPlatform]);

  // If we're already running as an installed app, auto-record and move on.
  useEffect(() => {
    if (autoInstallFiredRef.current) return;
    if (alreadyStandalone) {
      autoInstallFiredRef.current = true;
      setAlreadyInstalled(true);
      (async () => {
        try {
          await api.post('/users/me/install-status', { status: 'installed', platform: detectedPlatform });
          await refreshUser();
          if (typeof onDone === 'function') onDone();
        } catch { /* fall through */ }
      })();
    }
  }, [api, detectedPlatform, alreadyStandalone, refreshUser, onDone]);

  const record = async (status) => {
    setSubmitting(true);
    try {
      await api.post('/users/me/install-status', { status, platform: detectedPlatform });
      await refreshUser();
      if (typeof onDone === 'function') onDone();
    } catch (err) {
      toast.error(formatApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const platformLabel = platform === 'ios' ? 'On iPhone (Safari)'
    : platform === 'android' ? 'On Android (Chrome)'
    : 'On desktop (Chrome / Edge / Safari)';

  const PlatformIcon = platform === 'desktop' ? Monitor : Smartphone;

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="install-pwa-step">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          <OnboardingStepIndicator current={stepIndex} role={isManager ? 'manager' : 'worker'} />
          <div className="flex items-center gap-3 mb-3">
            <div className="h-12 w-12 bg-[#4338CA]/10 rounded-xl flex items-center justify-center shrink-0">
              <PlatformIcon className="h-6 w-6 text-[#4338CA]" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" data-testid="install-title">
              Install ShiftCover365
            </h2>
          </div>
          <p className="text-base text-slate-600 mb-4" data-testid="install-supporting-text">
            Open ShiftCover365 faster and receive important shift updates. Choose your device and follow the steps.
          </p>

          {/* Device picker — auto-selected via useDevice(), user can switch. */}
          <div
            className="flex gap-1 p-1 mb-5 rounded-full bg-slate-100 border border-slate-200"
            role="tablist"
            data-testid="install-platform-picker"
          >
            {PLATFORMS.map((p) => {
              const active = platform === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setPlatform(p.key)}
                  className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors ${active ? 'bg-white text-[#4338CA] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid={`install-platform-${p.key}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {alreadyInstalled ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 mb-5 flex items-start gap-3" data-testid="install-already-installed">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">
                ShiftCover365 is already installed on this device — one moment while we continue.
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-5" data-testid="install-instructions-card">
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                {platformLabel}
              </p>
              {platform === 'ios' && (
                <ol className="space-y-2.5 text-base text-slate-700" data-testid="install-instructions-ios">
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">1</span>
                    <span>Open ShiftCover365 in <span className="font-semibold">Safari</span> (not another app).</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">2</span>
                    <span>Tap the <Share className="inline h-4 w-4 -mt-0.5 mx-0.5" /><span className="font-semibold">Share</span> icon at the bottom of the screen.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">3</span>
                    <span>Scroll down and tap <span className="font-semibold">Add to Home Screen</span>.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">4</span>
                    <span>Tap <span className="font-semibold">Add</span>.</span>
                  </li>
                </ol>
              )}
              {platform === 'android' && (
                <ol className="space-y-2.5 text-base text-slate-700" data-testid="install-instructions-android">
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">1</span>
                    <span>Open ShiftCover365 in <span className="font-semibold">Chrome</span>.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">2</span>
                    <span>Tap the <MoreVertical className="inline h-4 w-4 -mt-0.5 mx-0.5" /><span className="font-semibold">three-dot menu</span> in the top-right.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">3</span>
                    <span>Tap <span className="font-semibold">Install app</span> (or <span className="font-semibold">Add to Home screen</span>).</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">4</span>
                    <span>Tap <span className="font-semibold">Install</span>.</span>
                  </li>
                </ol>
              )}
              {platform === 'desktop' && (
                <ol className="space-y-2.5 text-base text-slate-700" data-testid="install-instructions-desktop">
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">1</span>
                    <span>Look for the <span className="font-semibold">install icon</span> at the right end of the address bar (a small monitor with a down arrow).</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">2</span>
                    <span>Click it and choose <span className="font-semibold">Install</span>.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#4338CA]/10 text-[#4338CA] font-bold text-xs shrink-0">3</span>
                    <span>If you can&apos;t see it, open the browser menu and choose <span className="font-semibold">Install ShiftCover365</span>.</span>
                  </li>
                </ol>
              )}
              <p className="text-xs text-slate-500 mt-4">
                Already installed? Just tap <span className="font-semibold">Continue</span> below.
              </p>
            </div>
          )}

          {!alreadyInstalled && (
            <div className="space-y-3">
              <Button
                onClick={() => record('installed')}
                disabled={submitting}
                className="w-full h-12 rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white text-base font-semibold disabled:opacity-60"
                data-testid="install-add-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>I&apos;ve installed ShiftCover365 <ArrowRight className="h-4 w-4 ml-1.5" /></>
                )}
              </Button>
              <Button
                onClick={() => record('skipped')}
                disabled={submitting}
                variant="ghost"
                className="w-full h-12 rounded-full text-base font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                data-testid="install-later-btn"
              >
                Do this later
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
